import React, {useEffect} from "react";
import clsx from "clsx";
import {BrandTraitPresence,} from "data/models/survey/brand-traits-bar-chart";
import {useRankedBrandTraits} from "data/access/survey-question-pages/brand-traits";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import {RenderableTitle} from "data/models/survey/base";
import "./index.css";
import {traitTitle} from "data/models/shared/bc-sheet/brand-trait";
import LoadingSurvey from "components/Survey/LoadingSurvey";
import {Link} from "react-router-dom";
import {assertNoError} from "helpers/asserts";

export type BrandTraits_SurveyQuestionPage_Props = {
  title: RenderableTitle,
  description: RenderableTitle | null,
  visible: boolean;
  questionId: string;
  value: "visited" | undefined,
  onChange: (id: string, value: "visited") => void,
}


export default function BrandTraitsSurveyQuestionPage(
    {
      title,
      description,
      visible,
      questionId,
      value,
      onChange,
    }: BrandTraits_SurveyQuestionPage_Props
) {
  const [rankedBrandTraits, rankedTraitsLoading, rankedTraitsError] = useRankedBrandTraits();
  const traitBarColors = ["#00A651", "#A6CE39", "#F4D90C"];

  const highestRank = rankedBrandTraits?.[0].traitScore ?? null;
  const brandTraits = rankedBrandTraits !== null && highestRank !== null
      ? rankedBrandTraits.slice(0, 3)
          .map(({traitKey, traitScore}) => ({
            id: traitKey,
            presence: traitScore * 100 / highestRank,
          } as BrandTraitPresence)) : null;

  useEffect(() => {
    if (!visible || value === "visited") {
      return;
    }
    if (
        rankedTraitsLoading
        || rankedBrandTraits === null
    ) {
      return;
    }
    onChange(questionId, "visited");
  }, [visible, value, questionId, onChange, rankedTraitsLoading, rankedBrandTraits]);

  if (rankedTraitsLoading) {
    return visible && <LoadingSurvey/> || null;
  }

  assertNoError(rankedTraitsError);

  return <div className={"flex flex-col w-full"}>
    <div className={clsx(QUESTION_CONTAINER_CLASS, "flex flex-col")}>
      <div className={"my-4 text-center"}>{title}</div>
      {description !== null && <div className={"my-4 text-center"}>{description}</div>}
      <div className={"flex flex-col gap-4 py-4"}>
        {brandTraits?.map((trait, index) => <div key={index} className={"flex flex-row"}>
          <div className={"w-48 text-left text-neutral-500"}>{traitTitle(trait.id)}</div>
          <div className={"grow"}>
            <div style={{
              animationName: "brand-trait",
              animationDuration: "2s",
              width: `${trait.presence}%`,
              backgroundColor: traitBarColors[index],
              height: "35px"
            }}/>
          </div>
        </div>)}
      </div>
    </div>
    <div className={"mt-16 mx-auto text-center"}>
      <span className={"text-xl font-bold"}>What are brand traits?</span>
      <br/>
      <span className={"text-xl text-neutral-500"}>
        We define your brand traits based on how you described your business.
        Your brand traits express your brand's personality and summarize how you envision your brand.
      </span>
    </div>
  </div>
}
